import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { NavigationExtras, Router, Params } from '@angular/router';
import { SessionService } from './session.service';

@Injectable()
export class NavigationService {
  constructor(
    private _location: Location,
    private _router: Router,
    private _session: SessionService
  ) {}

  get url(): string {
    return this._router.url;
  }

  get parameterLessUrl(): string {
    return this.url.split(/[?#]/)[0];
  }

  public getQueryParameters(): Map<string, string> {
    const values = new Map<string, string>();
    const matches = this.url.match(/[?|&]([^&]*)=([^&#]*)/g);
    if (matches === null) return values;

    const parameters = Array.from(matches);
    for (let i = 0; i < parameters.length; i++) {
      const prm = parameters[i];
      const eqPos = prm.indexOf('=');
      values.set(prm.substr(1, eqPos - 1), prm.substr(eqPos + 1));
    }
    return values;
  }

  public getParameterValue(parameterName: string): string {
    const parameters = this.getQueryParameters();
    if (!parameters.has(parameterName)) return null;
    return parameters.get(parameterName);
  }

  private _getListRoute(): string {
    let root = decodeURI(this.url);
    let queryParams = root.indexOf('?');
    if (queryParams > 0) {
      root = root.substr(0, queryParams);
    }
    let addIndex = root.indexOf('/add');
    let editIndex = root.indexOf('/edit/');
    if (editIndex > 0) {
      root = root.substr(0, editIndex);
    } else if (addIndex > 0) {
      root = root.substr(0, addIndex);
    } else if (root.endsWith('/create')) {
      root = root.substr(0, root.length - '/create'.length);
    }
    return root;
  }

  reloadAndNavigateToList() {
    const listRoute = this._getListRoute();
    const listPage = this.getParameterValue('listPage');
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([listRoute], {
        queryParams: listPage && /^\d+$/.test(listPage) ? { page: listPage } : undefined,
      });
    });
  }

  setListPage(page: number): void {
    this._router.navigate([this.parameterLessUrl], {
      queryParams: { page: page > 1 ? page : null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  reloadCurrentRoute() {
    const currentUrl = this._router.url;
    this._router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this._router.navigate([currentUrl]);
    });
  }

  navigate(commands: any[], extras?: NavigationExtras, newTab?: boolean): Promise<boolean> {
    let containsRoot = false;
    let segments = this.parameterLessUrl.split('/');
    const root = segments.length > 1 ? decodeURIComponent(segments[1]) : '';
    for (let i = 0; i < commands.length; i++) {
      let item = commands[i];
      if (item === root) {
        containsRoot = true;
        break;
      } else if (typeof item === 'string') {
        let segments = item.split('/');
        containsRoot = segments.includes(root);
        break;
      }
    }
    if (!containsRoot) {
      commands.unshift(root);
    }

    if (extras === null) {
      extras = undefined;
    }

    let commandParts = commands.filter(x => x !== '' && x !== undefined && x !== null);

    if (newTab === true) {
      const url = this._router.serializeUrl(this._router.createUrlTree(commandParts, extras));
      window.open(url, '_blank');
      return;
    }

    return this._router.navigate(commandParts, extras);
  }

  /** Navigates to a full in-app URL, keeping its own query string (e.g. a deep link stored before login). */
  navigateByUrl(url: string): Promise<boolean> {
    return this._router.navigateByUrl(url);
  }

  navigateBack(): void {
    this._location.back();
  }

  get listReturnState(): { returnToList: string } | undefined {
    const returnToList = (this._location.getState() as { returnToList?: unknown })?.returnToList;
    return typeof returnToList === 'string' ? { returnToList } : undefined;
  }

  navigateChild(commands: string[], extras?: NavigationExtras, newTab?: boolean): Promise<boolean> {
    commands.unshift(this._getListRoute());
    return this.navigate(commands, extras, newTab);
  }

  navigateHome() {
    this._router.navigate([this._session.homeUrl]);
  }

  /** Resolves false when the navigation was cancelled, for instance by a guard on a dirty form. */
  navigateLogin(): Promise<boolean> {
    return this._router.navigate(['login'], { queryParamsHandling: 'preserve' });
  }

  navigatePageNotFound() {
    this._router.navigate(['dh', '404'], { skipLocationChange: true });
  }

  navigateToForm(id?: string, newTab?: boolean, isTemplate?: boolean, extras?: NavigationExtras): void {
    let listRoot = this._getListRoute();
    let path = '';
    if (isTemplate) {
      path = listRoot + `/add/${id}`;
    } else {
      path = listRoot + (id ? `/edit/${id}` : '/add');
    }

    const listPage = this.getParameterValue('page');
    const formExtras = listPage && /^\d+$/.test(listPage)
      ? { ...extras, queryParams: { ...extras?.queryParams, listPage } }
      : extras;

    if (newTab === true) {
      const url = this._router.serializeUrl(this._router.createUrlTree([path], formExtras));
      window.open(url, '_blank');
      return;
    }

    this._router.navigate([path], formExtras);
  }

  navigateToList(listRoute?: string) {
    if (listRoute && this.listReturnState?.returnToList.split(/[?#]/)[0] === listRoute) {
      this._location.back();
      return;
    }

    const listPage = this.getParameterValue('listPage');
    this._router.navigate([listRoute ?? this._getListRoute()], {
      queryParams: listPage && /^\d+$/.test(listPage) ? { page: listPage } : undefined,
    });
  }
}
